import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

import { useAuthContext } from '../../context/auth-context';
import {
  useGetAllAffiliationsQuery,
  useCreateAffiliationMutation,
  useUpdateAffiliationMutation,
  useDeleteAffiliationMutation,
} from '../../redux/services/affiliation-api';
import { useIsMobile } from '../../hooks/use-is-mobile';

const PRIMARY_COLOR = '#0F6E56';

// ─── Affiliation Dialog (shared for create and edit) ─────────────────────────

function AffiliationDialog({ open, onClose, onSave, initial, title }) {
  const [name, setName] = useState(initial?.name || '');
  const [zone, setZone] = useState(initial?.zone || '');
  const [saving, setSaving] = useState(false);
  const isMobile = useIsMobile();

  React.useEffect(() => {
    if (open) {
      setName(initial?.name || '');
      setZone(initial?.zone || '');
    }
  }, [open, initial]);

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    await onSave({ name: name.trim(), zone: zone.trim() });
    setSaving(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='xs' fullWidth fullScreen={isMobile}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        <TextField
          label='Nombre'
          value={name}
          onChange={e => setName(e.target.value)}
          required
          fullWidth
          autoFocus
        />
        <TextField
          label='Zona (opcional)'
          value={zone}
          onChange={e => setZone(e.target.value)}
          fullWidth
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button
          onClick={handleSave}
          disabled={saving || !name.trim()}
          variant='contained'
          sx={{ bgcolor: PRIMARY_COLOR, '&:hover': { bgcolor: '#0a5a45' } }}
        >
          {saving ? <CircularProgress size={20} color='inherit' /> : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Delete Confirm Dialog ────────────────────────────────────────────────────

function DeleteConfirmDialog({ open, onClose, onConfirm, name, errorMsg }) {
  const [deleting, setDeleting] = useState(false);
  const isMobile = useIsMobile();

  const handleConfirm = async () => {
    setDeleting(true);
    await onConfirm();
    setDeleting(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='xs' fullWidth fullScreen={isMobile}>
      <DialogTitle>Confirmar eliminación</DialogTitle>
      <DialogContent>
        <Typography>¿Eliminar <strong>{name}</strong>? Esta acción no se puede deshacer.</Typography>
        {errorMsg && <Alert severity='error' sx={{ mt: 2 }}>{errorMsg}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={deleting}>Cancelar</Button>
        <Button onClick={handleConfirm} color='error' variant='contained' disabled={deleting}>
          {deleting ? <CircularProgress size={20} color='inherit' /> : 'Eliminar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Affiliation Table ────────────────────────────────────────────────────────

function AffiliationTable({ rows, isLoading, onEdit, onDelete }) {
  if (isLoading) return <CircularProgress sx={{ mt: 3 }} />;
  if (!rows.length) return <Typography color='text.secondary' sx={{ mt: 2 }}>Sin registros.</Typography>;

  return (
    <TableContainer component={Paper} variant='outlined' sx={{ mt: 2 }}>
      <Table size='small'>
        <TableHead>
          <TableRow sx={{ bgcolor: '#f5f5f5' }}>
            <TableCell><strong>Nombre</strong></TableCell>
            <TableCell><strong>Zona</strong></TableCell>
            <TableCell align='right'></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(row => (
            <TableRow key={row._id} hover>
              <TableCell>{row.name}</TableCell>
              <TableCell>{row.zone || '—'}</TableCell>
              <TableCell align='right'>
                <IconButton size='small' onClick={() => onEdit(row)}><EditIcon fontSize='small' /></IconButton>
                <IconButton size='small' color='error' onClick={() => onDelete(row)}><DeleteIcon fontSize='small' /></IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ─── Tab: Organizaciones (Parents) ───────────────────────────────────────────

function ParentsTab() {
  const { data: parents = [], isLoading } = useGetAllAffiliationsQuery({ type: 'parents' });
  const [createAffiliation] = useCreateAffiliationMutation();
  const [updateAffiliation] = useUpdateAffiliationMutation();
  const [deleteAffiliation] = useDeleteAffiliationMutation();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteError, setDeleteError] = useState('');

  const handleCreate = async ({ name, zone }) => {
    await createAffiliation({ name, zone, parent: null });
  };

  const handleEdit = async ({ name, zone }) => {
    await updateAffiliation({ _id: editTarget._id, name, zone });
  };

  const handleDelete = async () => {
    const res = await deleteAffiliation(deleteTarget._id);
    if (res.error) {
      setDeleteError(res.error.data?.message || 'No se pudo eliminar.');
    } else {
      setDeleteTarget(null);
      setDeleteError('');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
        <Typography variant='body2' color='text.secondary'>
          Las organizaciones son los niveles superiores que se asocian a los campamentos.
        </Typography>
        <Button
          startIcon={<AddIcon />}
          variant='contained'
          size='small'
          sx={{ bgcolor: PRIMARY_COLOR, '&:hover': { bgcolor: '#0a5a45' }, ml: 2, flexShrink: 0 }}
          onClick={() => { setEditTarget(null); setDialogOpen(true); }}
        >
          Nueva organización
        </Button>
      </Box>

      <AffiliationTable
        rows={parents}
        isLoading={isLoading}
        onEdit={row => { setEditTarget(row); setDialogOpen(true); }}
        onDelete={row => { setDeleteTarget(row); setDeleteError(''); }}
      />

      <AffiliationDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={editTarget ? handleEdit : handleCreate}
        initial={editTarget}
        title={editTarget ? 'Editar organización' : 'Nueva organización'}
      />

      <DeleteConfirmDialog
        open={Boolean(deleteTarget)}
        onClose={() => { setDeleteTarget(null); setDeleteError(''); }}
        onConfirm={handleDelete}
        name={deleteTarget?.name}
        errorMsg={deleteError}
      />
    </Box>
  );
}

// ─── Tab: Afiliaciones del Campamento (Children) ─────────────────────────────

function ChildrenTab() {
  const { activeCamp } = useAuthContext();
  const campParentId = activeCamp?.affiliation?._id || activeCamp?.affiliation;

  const { data: children = [], isLoading } = useGetAllAffiliationsQuery(
    activeCamp?._id ? { campId: activeCamp._id } : { type: 'parents' },
    { skip: !activeCamp?._id }
  );

  const [createAffiliation] = useCreateAffiliationMutation();
  const [updateAffiliation] = useUpdateAffiliationMutation();
  const [deleteAffiliation] = useDeleteAffiliationMutation();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteError, setDeleteError] = useState('');

  const handleCreate = async ({ name, zone }) => {
    await createAffiliation({ name, zone, parent: campParentId });
  };

  const handleEdit = async ({ name, zone }) => {
    await updateAffiliation({ _id: editTarget._id, name, zone });
  };

  const handleDelete = async () => {
    const res = await deleteAffiliation(deleteTarget._id);
    if (res.error) {
      setDeleteError(res.error.data?.message || 'No se pudo eliminar.');
    } else {
      setDeleteTarget(null);
      setDeleteError('');
    }
  };

  if (!campParentId) {
    return (
      <Alert severity='info' sx={{ mt: 2 }}>
        El campamento activo no tiene una organización asignada. Edita el campamento en{' '}
        <strong>Campamentos</strong> y selecciona una organización para poder gestionar sus afiliaciones.
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
        <Typography variant='body2' color='text.secondary'>
          Afiliaciones del campamento <strong>{activeCamp?.name}</strong>. Solo estas aparecen al crear clubs.
        </Typography>
        <Button
          startIcon={<AddIcon />}
          variant='contained'
          size='small'
          sx={{ bgcolor: PRIMARY_COLOR, '&:hover': { bgcolor: '#0a5a45' }, ml: 2, flexShrink: 0 }}
          onClick={() => { setEditTarget(null); setDialogOpen(true); }}
        >
          Nueva afiliación
        </Button>
      </Box>

      <AffiliationTable
        rows={children}
        isLoading={isLoading}
        onEdit={row => { setEditTarget(row); setDialogOpen(true); }}
        onDelete={row => { setDeleteTarget(row); setDeleteError(''); }}
      />

      <AffiliationDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={editTarget ? handleEdit : handleCreate}
        initial={editTarget}
        title={editTarget ? 'Editar afiliación' : 'Nueva afiliación'}
      />

      <DeleteConfirmDialog
        open={Boolean(deleteTarget)}
        onClose={() => { setDeleteTarget(null); setDeleteError(''); }}
        onConfirm={handleDelete}
        name={deleteTarget?.name}
        errorMsg={deleteError}
      />
    </Box>
  );
}

// ─── Settings Page ────────────────────────────────────────────────────────────

export default function SettingsPage() {
  const [tab, setTab] = useState(0);

  return (
    <Box>
      <Typography variant='h5' fontWeight={700} mb={2}>Configuración</Typography>
      <Paper variant='outlined' sx={{ p: 3 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
          TabIndicatorProps={{ style: { backgroundColor: PRIMARY_COLOR } }}
        >
          <Tab label='Organizaciones' sx={{ '&.Mui-selected': { color: PRIMARY_COLOR } }} />
          <Tab label='Afiliaciones del Campamento' sx={{ '&.Mui-selected': { color: PRIMARY_COLOR } }} />
        </Tabs>

        {tab === 0 && <ParentsTab />}
        {tab === 1 && <ChildrenTab />}
      </Paper>
    </Box>
  );
}
